<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/Page-header";
import CheckModal from "@/components/CheckModal";
import DocumentActions from "@/components/document/DocumentActions.vue";
import legalMatrixAPI from "@/services/standards/legalMatrix/legalMatrixAPI";
import newScoresAPI from "@/services/newScores/newScoresAPI";
import Swal from "sweetalert2";

const ESTADOS = ["Cumple", "Parcial", "No Cumple", "No Aplica"];

export default {
  page: { title: "Matriz Legal SST" },
  components: { Layout, PageHeader, CheckModal, DocumentActions },
  data() {
    return {
      title: "Matriz Legal SST",
      items: [
        { text: "Estándares", href: "/forms/estandares" },
        { text: "2.7.1 Matriz Legal", active: true },
      ],
      estados: ESTADOS,
      companyID: this.$store.getters["company/getCompany"].id || null,
      reviewId: this.$route.params.review || null,
      form: {
        fecha: new Date().toISOString().substring(0, 10),
        version: "1.0",
        observacion_general: null,
      },
      rows: [], // {id, tema, factor, tipo_norma, ..., estado, observacion}
      temaFilter: null,
      estadoFilter: null,
      workflowStatus: "draft",
      signedAt: null,
      submitting: false,
      loading: true,
    };
  },
  computed: {
    isSigned() {
      return this.workflowStatus === "signed";
    },
    legalMatrixApi() {
      return {
        pdfUrl: (id) => legalMatrixAPI.pdfUrl(id),
        sign: (id, payload) => legalMatrixAPI.sign(id, payload),
      };
    },
    temas() {
      return [...new Set(this.rows.map((r) => r.tema).filter(Boolean))];
    },
    counts() {
      const c = { Cumple: 0, Parcial: 0, "No Cumple": 0, "No Aplica": 0, "Sin Evaluar": 0 };
      this.rows.forEach((r) => {
        const key = ESTADOS.includes(r.estado) ? r.estado : "Sin Evaluar";
        c[key]++;
      });
      return c;
    },
    total() {
      return this.rows.length;
    },
    pct() {
      return this.total > 0 ? Math.round((this.counts.Cumple / this.total) * 100) : 0;
    },
    filteredGroups() {
      let rows = this.rows;
      if (this.temaFilter) rows = rows.filter((r) => r.tema === this.temaFilter);
      if (this.estadoFilter) {
        rows = rows.filter((r) =>
          this.estadoFilter === "Sin Evaluar" ? !ESTADOS.includes(r.estado) : r.estado === this.estadoFilter
        );
      }
      const map = {};
      rows.forEach((r) => {
        const t = r.tema || "—";
        if (!map[t]) map[t] = [];
        map[t].push(r);
      });
      return Object.keys(map).map((tema) => ({ tema, rows: map[tema] }));
    },
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    estadoClass(estado) {
      if (estado === "Cumple") return "bg-cumple";
      if (estado === "Parcial") return "bg-parcial";
      if (estado === "No Cumple") return "bg-nocumple";
      if (estado === "No Aplica") return "bg-noaplica";
      return "";
    },
    async loadCatalogAndReview() {
      try {
        const reqs = await legalMatrixAPI.requirements();
        this.rows = (reqs || []).map((r) => ({ ...r, estado: null, observacion: null }));

        if (this.reviewId) {
          const rev = await legalMatrixAPI.get(this.reviewId);
          if (rev) {
            this.form.fecha = rev.fecha ? String(rev.fecha).substring(0, 10) : this.form.fecha;
            this.form.version = rev.version || "1.0";
            this.form.observacion_general = rev.observacion_general || null;
            this.workflowStatus = rev.workflow_status || "draft";
            this.signedAt = rev.signed_at || null;
            const byReq = {};
            (rev.items || []).forEach((it) => { byReq[it.legal_requirement_id] = it; });
            this.rows.forEach((r) => {
              const it = byReq[r.id];
              if (it) { r.estado = it.estado || null; r.observacion = it.observacion || null; }
            });
          }
        }
        this.loading = false;
      } catch (e) {
        console.log(e);
        this.loading = false;
      }
    },
    save() {
      if (this.isSigned) return;
      this.submitting = true;
      const payload = {
        companies_id: this.companyID,
        fecha: this.form.fecha,
        version: this.form.version,
        observacion_general: this.form.observacion_general,
        items: this.rows.map((r) => ({
          legal_requirement_id: r.id,
          estado: r.estado,
          observacion: r.observacion,
        })),
      };
      const req = this.reviewId
        ? legalMatrixAPI.update(this.reviewId, payload)
        : legalMatrixAPI.save(payload);
      req
        .then((res) => {
          newScoresAPI.calculateScores({ companies_id: this.companyID, item_id: 18, status: "Pendiente" }).catch(() => {});
          Swal.fire({ title: "¡Éxito!", text: "Matriz legal guardada con éxito.", icon: "success", timer: 1800, showConfirmButton: false });
          this.submitting = false;
          if (!this.reviewId && res && res.id) {
            this.reviewId = res.id;
            this.$router.replace({ path: `/forms/estandares/${this.companyID}/matriz-legal-revision/${res.id}` });
          }
        })
        .catch((e) => {
          console.log(e);
          this.submitting = false;
          Swal.fire("Error", "No se pudo guardar la matriz legal", "error");
        });
    },
    onSigned(payload) {
      this.workflowStatus = payload.workflow_status;
      this.signedAt = payload.signed_at;
    },
  },
  mounted() {
    this.loadCatalogAndReview();
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-lg-9" v-if="!loading">
        <div class="card">
          <div class="card-body">
            <CheckModal>
              <p class="text-justify">
                Evalúa el cumplimiento de cada requisito legal del SG-SST marcando su
                <strong>Estado de Cumplimiento</strong> (Cumple / Parcial / No Cumple / No Aplica) y, si aplica, una
                observación o evidencia propia. La plataforma calcula el cuadro de mando y genera la Matriz Legal con la
                imagen de la empresa para la firma del Representante Legal.
              </p>
            </CheckModal>

            <div v-if="isSigned" class="alert alert-success py-2 my-2 small">
              🔒 Esta revisión ya fue firmada; los datos quedan bloqueados para edición.
            </div>

            <form @submit.prevent="save">
              <div class="form-row">
                <b-form-group class="col-md-3 px-2" label="Fecha de revisión">
                  <b-form-input type="date" v-model="form.fecha" :disabled="isSigned" required />
                </b-form-group>
                <b-form-group class="col-md-2 px-2" label="Versión">
                  <b-form-input type="text" v-model="form.version" :disabled="isSigned" />
                </b-form-group>
                <b-form-group class="col-md-7 px-2" label="Observaciones generales">
                  <b-form-input type="text" v-model="form.observacion_general" :disabled="isSigned" />
                </b-form-group>
              </div>

              <!-- Cuadro de mando live -->
              <div class="dashboard-strip mb-3">
                <span class="ds-chip bg-cumple">Cumple <b>{{ counts.Cumple }}</b></span>
                <span class="ds-chip bg-parcial">Parcial <b>{{ counts.Parcial }}</b></span>
                <span class="ds-chip bg-nocumple">No Cumple <b>{{ counts['No Cumple'] }}</b></span>
                <span class="ds-chip bg-noaplica">No Aplica <b>{{ counts['No Aplica'] }}</b></span>
                <span class="ds-chip bg-light">Sin Evaluar <b>{{ counts['Sin Evaluar'] }}</b></span>
                <span class="ds-chip bg-light">Total <b>{{ total }}</b></span>
                <span class="ds-chip ds-pct">% Cumplimiento <b>{{ pct }}%</b></span>
              </div>

              <!-- Filtros -->
              <div class="form-row">
                <b-form-group class="col-md-5 px-2" label="Filtrar por tema">
                  <b-form-select v-model="temaFilter" plain class="form-control form-control-sm">
                    <b-form-select-option :value="null">Todos los temas</b-form-select-option>
                    <b-form-select-option v-for="t in temas" :key="t" :value="t">{{ t }}</b-form-select-option>
                  </b-form-select>
                </b-form-group>
                <b-form-group class="col-md-4 px-2" label="Filtrar por estado">
                  <b-form-select v-model="estadoFilter" plain class="form-control form-control-sm">
                    <b-form-select-option :value="null">Todos los estados</b-form-select-option>
                    <b-form-select-option v-for="e in estados" :key="e" :value="e">{{ e }}</b-form-select-option>
                    <b-form-select-option value="Sin Evaluar">Sin Evaluar</b-form-select-option>
                  </b-form-select>
                </b-form-group>
              </div>

              <!-- Matriz por tema -->
              <div v-for="g in filteredGroups" :key="g.tema" class="mb-3">
                <h6 class="tema-head">{{ g.tema }}</h6>
                <div class="table-responsive">
                  <table class="table table-sm table-bordered mb-0 ml-table">
                    <thead>
                      <tr>
                        <th style="width:14%">Norma</th>
                        <th style="width:30%">Descripción</th>
                        <th style="width:22%">Evidencia sugerida</th>
                        <th style="width:15%">Estado</th>
                        <th style="width:19%">Observación / evidencia propia</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="r in g.rows" :key="r.id">
                        <td class="small">
                          <strong>{{ r.tipo_norma }} {{ r.numero }}<span v-if="r.anio"> / {{ r.anio }}</span></strong>
                          <div class="text-muted">{{ r.articulos }}</div>
                          <div class="text-muted">{{ r.expedida_por }}</div>
                        </td>
                        <td class="small text-justify">{{ r.descripcion }}</td>
                        <td class="small text-justify text-muted">{{ r.evidencia }}</td>
                        <td>
                          <b-form-select v-model="r.estado" :disabled="isSigned"
                            class="form-control form-control-sm" :class="estadoClass(r.estado)">
                            <b-form-select-option :value="null">— Sin evaluar —</b-form-select-option>
                            <b-form-select-option v-for="e in estados" :key="e" :value="e">{{ e }}</b-form-select-option>
                          </b-form-select>
                        </td>
                        <td>
                          <b-form-textarea v-model="r.observacion" :disabled="isSigned" rows="2" size="sm"
                            placeholder="Observación / evidencia..." />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div class="text-right mt-3">
                <button type="submit" :disabled="isSigned || submitting" class="btn btn-primary mx-1">Guardar</button>
                <button type="button" class="btn btn-secondary mx-1" @click="goBack">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div class="col-lg-3" v-if="!loading && reviewId">
        <h6 class="text-muted small mb-1">Matriz Legal SST</h6>
        <DocumentActions
          :document-id="Number(reviewId)"
          :company-id="companyID"
          :workflow-status="workflowStatus"
          :signed-at="signedAt"
          :api-service="legalMatrixApi"
          document-title="Matriz Legal SST"
          signer-role="gerente"
          @signed="onSigned"
        />
      </div>

      <div v-if="loading" class="col-12 text-center">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.dashboard-strip { display: flex; flex-wrap: wrap; gap: 6px; }
.ds-chip { display: inline-block; padding: 4px 10px; border-radius: 6px; font-size: 0.75rem; border: 1px solid #ddd; }
.ds-chip b { font-size: 0.9rem; }
.ds-pct { background: #1f4e79; color: #fff; border-color: #1f4e79; }
.tema-head { background: #1f4e79; color: #fff; padding: 5px 10px; border-radius: 4px; font-size: 0.85rem; margin-bottom: 0; }
.ml-table td { vertical-align: middle; }
.bg-cumple { background: #c6efce !important; }
.bg-parcial { background: #ffeb9c !important; }
.bg-nocumple { background: #ffc7ce !important; }
.bg-noaplica { background: #d9d9d9 !important; }
</style>
